import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { mix } from 'polished';
import { TextWithLinks } from '@ubeya/shared-web/components';
import {
  HELP_DESK_INTERNAL_NOTE_COLORS,
  HELP_DESK_MESSAGE_TYPES,
  HELP_DESK_SENDER_TYPES
} from '@ubeya/shared/constants/helpDesk';
import Tooltip from '@ubeya/shared-web/components/Tooltip/Tooltip';
import { ReactComponent as EyeHideIcon } from '@ubeya/shared-web/assets/eye-hide.svg';
import { isImageFile, isVideoFile, getSystemEventDescription } from '../../helpDeskUtils';
import FileAttachmentCard from './FileAttachmentCard';
import ImageAttachments from './ImageAttachments';

// region: Full-size styled components (main ticket view)

const MessageRow = styled.div`
  display: flex;
  margin-top: ${({ $isFirstInUserGroup }) => ($isFirstInUserGroup ? '16px' : 0)};
  margin-bottom: ${({ $isGroupedWithNext }) => ($isGroupedWithNext ? '8px' : '16px')};

  ${({ $align }) =>
    $align === 'right' &&
    css`
      justify-content: flex-end;
    `}
`;

const BubbleWrapper = styled.div`
  min-width: 80px;
  max-width: ${({ $hasAttachments }) => ($hasAttachments ? 'min(75%, 358px)' : '75%')};
  display: flex;
  flex-direction: column;

  ${({ $align }) =>
    $align === 'right' &&
    css`
      align-items: flex-end;
    `}
`;

const SenderName = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  margin-bottom: 4px;
  width: 100%;
  padding: 0 8px;

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'admin':
        return css`
          text-align: right;
          justify-content: flex-end;
          color: ${theme.colors.white};
        `;
      case 'internal':
        return css`
          text-align: right;
          justify-content: flex-end;
          color: ${theme.colors.black};
        `;
      default:
        return css`
          color: ${theme.colors.black};
        `;
    }
  }}
`;

const InternalNoteIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 17px;
    height: 12px;
  }

  path {
    fill: ${({ theme }) => theme.colors.orangeChanges};
  }
`;

const getBubbleArrowColor = ({ $variant, theme }) => {
  if ($variant === 'internal') {
    return HELP_DESK_INTERNAL_NOTE_COLORS.background;
  }

  if ($variant === 'admin') {
    return mix(0.05, theme.colors.black, theme.colors.primary);
  }

  return theme.colors.white;
};

const Bubble = styled.div`
  position: relative;
  padding: 4px;
  border-radius: 8px;
  width: 100%;
  font-size: 14px;
  line-height: 20px;
  word-break: break-word;
  display: flex;
  flex-direction: column;

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'admin':
        return css`
          background: linear-gradient(${theme.colors.black}0D, ${theme.colors.black}0D), ${theme.colors.primary};
          color: ${theme.colors.white};
          align-items: flex-end;

          a {
            color: ${theme.colors.white};
          }
        `;
      case 'employee':
        return css`
          background: ${theme.colors.white};
          color: ${theme.colors.black};
        `;
      case 'internal':
        return css`
          background: ${HELP_DESK_INTERNAL_NOTE_COLORS.background};
          color: ${theme.colors.black};
          align-items: flex-end;
        `;
      case 'system':
        return css`
          background: ${theme.colors.grey2};
          color: ${theme.colors.grey4};
          font-style: italic;
          font-size: 12px;
        `;
      default:
        return '';
    }
  }}

  ${({ $showArrow, $variant }) =>
    $showArrow &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        width: 0;
        height: 0;
      }

      ${$variant === 'employee'
        ? css`
            &::before {
              left: -10px;
              border-top: 16px solid ${getBubbleArrowColor};
              border-left: 16px solid transparent;
            }
          `
        : css`
            &::before {
              right: -10px;
              border-top: 16px solid ${getBubbleArrowColor};
              border-right: 16px solid transparent;
            }
          `}
    `}
    
  a {
    text-decoration: underline;
  }
`;

const MessageContent = styled.div`
  white-space: pre-wrap;
  padding: 0 8px;
  width: 100%;
  font-size: 16px;
  font-weight: 400;
  margin-top: ${({ $hasAttachments }) => ($hasAttachments ? '4px' : 0)};
`;

const Timestamp = styled.div`
  font-size: 12px;
  line-height: 16px;
  margin-top: 4px;
  padding: 0 8px;
  color: ${({ $light, theme }) => ($light ? theme.colors.white : theme.colors.grey4)};
  text-align: right;
`;

const AttachmentsWrapper = styled.div`
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

// region: System event (full view only, compact renders inline)

const SystemEventRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
  color: ${({ theme }) => theme.colors.grey4};
  text-align: center;
`;

const SystemEventTime = styled.div`
  font-size: 12px;
  line-height: 20px;
  margin-bottom: 4px;
`;

const SystemEventText = styled.div`
  font-size: 14px;
  line-height: 20px;
  max-width: 80%;
`;

const MessageBubble = ({
  msg,
  accountId,
  ticketId,
  t,
  onOpenPdf,
  compact = false,
  showSenderLabel = true,
  showArrow = true,
  isFirstInUserGroup = true,
  isGroupedWithNext = false
}) => {
  const isSystem = msg.messageType === HELP_DESK_MESSAGE_TYPES.SYSTEM_EVENT;
  const isInternal = msg.messageType === HELP_DESK_MESSAGE_TYPES.INTERNAL_NOTE;
  const isAdmin = msg.senderType === HELP_DESK_SENDER_TYPES.ADMIN;
  const isAutomated = msg.isAutomated === true;
  const timeLabel = msg.createdAt ? moment.utc(msg.createdAt).local().format('HH:mm') : '';

  const { mediaAtts, fileAtts } = useMemo(() => {
    const media = [];
    const files = [];
    (msg.attachments || []).forEach((att) =>
      isImageFile(att) || isVideoFile(att) ? media.push(att) : files.push(att)
    );
    return { mediaAtts: media, fileAtts: files };
  }, [msg.attachments]);

  // Full-size system events are centered outside regular message bubbles.
  if (isSystem) {
    return (
      <SystemEventRow>
        {timeLabel && <SystemEventTime>{timeLabel}</SystemEventTime>}
        <SystemEventText>{getSystemEventDescription(msg.content, t)}</SystemEventText>
      </SystemEventRow>
    );
  }

  // System events returned above; below is for non-system messages only.
  let variant = 'employee';
  let align = 'left';

  if (isInternal) {
    variant = 'internal';
    align = 'right';
  } else if (isAdmin) {
    variant = 'admin';
    align = 'right';
  }

  const attachments = msg.attachments || [];
  const hasAttachments = attachments.length > 0;

  const senderLabel = isAutomated
    ? t('automated_message')
    : msg.senderName || msg.sender?.name || (isAdmin ? t('help_desk_admin') : t('help_desk_employee'));

  const renderContent = () => {
    const raw = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content);
    const text = isAutomated ? t(raw, { defaultValue: raw }) : raw;
    return (
      <MessageContent dir="auto" $hasAttachments={hasAttachments}>
        <TextWithLinks>{text}</TextWithLinks>
      </MessageContent>
    );
  };

  return (
    <MessageRow
      $align={align}
      $compact={compact}
      $isFirstInUserGroup={isFirstInUserGroup}
      $isGroupedWithNext={isGroupedWithNext}>
      <BubbleWrapper $align={align} $compact={compact} $hasAttachments={hasAttachments}>
        <Bubble $variant={variant} $compact={compact} $showArrow={!!senderLabel && showArrow}>
          {senderLabel && showSenderLabel && (
            <SenderName $variant={variant}>
              {isInternal && (
                <Tooltip body={t('help_desk_internal_note')}>
                  <InternalNoteIcon>
                    <EyeHideIcon />
                  </InternalNoteIcon>
                </Tooltip>
              )}
              {isAdmin && !isAutomated && `${t('help_desk_admin_label')} `}
              {senderLabel}
            </SenderName>
          )}

          {mediaAtts.length > 0 && (
            <ImageAttachments media={mediaAtts} accountId={accountId} ticketId={ticketId} compact={compact} />
          )}

          {fileAtts.length > 0 && (
            <AttachmentsWrapper $compact={compact}>
              {fileAtts.map((att) => (
                <FileAttachmentCard
                  key={att.id}
                  attachment={att}
                  variant={variant}
                  accountId={accountId}
                  ticketId={ticketId}
                  onOpenPdf={onOpenPdf}
                  compact={compact}
                />
              ))}
            </AttachmentsWrapper>
          )}

          {renderContent()}

          {timeLabel && (
            <Timestamp $light={variant === 'admin'} $compact>
              {timeLabel}
            </Timestamp>
          )}
        </Bubble>
      </BubbleWrapper>
    </MessageRow>
  );
};

export default MessageBubble;
